import { UserRank, UserRankSnapshotResp, UserRankType } from "@garther/interfaces";
import { cn } from "@garther/ui/libs";
import { AlertTriangle, Crown } from "lucide-react";

import { Box } from "../Box";
import { Progress } from "../Progress";
import { RankBadge } from "./rank-badge";

const RANK_THRESHOLDS: Record<UserRankType, number> = {
  [UserRank.NONE]: 0,
  [UserRank.BRONZE]: 500,
  [UserRank.SILVER]: 1500,
  [UserRank.GOLD]: 4000,
  [UserRank.PLATINUM]: 10000
};

const NEXT_RANK: Partial<Record<UserRankType, UserRankType>> = {
  [UserRank.NONE]: UserRank.BRONZE,
  [UserRank.BRONZE]: UserRank.SILVER,
  [UserRank.SILVER]: UserRank.GOLD,
  [UserRank.GOLD]: UserRank.PLATINUM
};

const RANK_PROGRESS_COLOR: Partial<Record<UserRankType, string>> = {
  [UserRank.NONE]: "[&_[data-slot=progress-indicator]]:bg-gray-400",
  [UserRank.BRONZE]: "[&_[data-slot=progress-indicator]]:bg-amber-600",
  [UserRank.SILVER]: "[&_[data-slot=progress-indicator]]:bg-slate-400",
  [UserRank.GOLD]: "[&_[data-slot=progress-indicator]]:bg-yellow-500"
};

function calcProgress(snapshot: UserRankSnapshotResp): number {
  const { currentRank, lifetimePoints, pointsToNextRank } = snapshot;
  if (currentRank === UserRank.PLATINUM) return 100;
  const tierStart = RANK_THRESHOLDS[currentRank];
  const tierEnd = lifetimePoints + pointsToNextRank;
  if (tierEnd <= tierStart) return 0;
  return Math.min(100, Math.round(((lifetimePoints - tierStart) / (tierEnd - tierStart)) * 100));
}

interface RankCardProps {
  snapshot: UserRankSnapshotResp;
  showInactivityWarning?: boolean;
  className?: string;
}

export function RankCard({ snapshot, showInactivityWarning = false, className }: RankCardProps) {
  const { currentRank, lifetimePoints, pointsToNextRank, lastActivityAt } = snapshot;

  const nextRank = NEXT_RANK[currentRank];
  const progress = calcProgress(snapshot);

  const daysSinceActivity = Math.floor(
    (Date.now() - new Date(lastActivityAt).getTime()) / (1000 * 60 * 60 * 24)
  );
  const showWarning = showInactivityWarning && daysSinceActivity >= 100;

  return (
    <Box
      label="Rank"
      className={cn("rounded-lg border border-border bg-background-secondary overflow-hidden", className)}
    >
      {showWarning && (
        <div className="flex items-start gap-3 px-6 py-3.5 bg-amber-50 border-b border-yellow-300 text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="m-0 text-sm leading-snug">
            <span className="font-semibold">Your rank is at risk.</span> {180 - daysSinceActivity} days until
            reset due to inactivity.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-6 pb-5">
        <RankBadge rank={currentRank} className="text-[13px] px-3 py-1" />
        <div className="leading-none flex flex-col items-right text-right">
          <div className="text-responsive-3xl font-medium text-text-primary font-literata tracking-[0.1em]">
            {lifetimePoints.toLocaleString()}
          </div>
          <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.12em] text-text-secondary">
            Points · lifetime
          </div>
        </div>
      </div>

      {currentRank !== UserRank.PLATINUM ? (
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-responsive-sm font-mono uppercase tracking-[0.1em] font-medium text-text-primary">
              {currentRank}
            </span>
            <span className="text-responsive-sm font-mono uppercase tracking-[0.1em] text-text-secondary">
              {nextRank} —{" "}
              <span className="font-semibold text-text-primary">{pointsToNextRank.toLocaleString()} pts</span>{" "}
              to go
            </span>
          </div>
          <Progress
            value={progress}
            className={cn("rounded-full bg-gray-100", RANK_PROGRESS_COLOR[currentRank])}
          />
          <div className="mt-1 text-responsive-sm text-text-secondary">
            {lifetimePoints.toLocaleString()} of {(lifetimePoints + pointsToNextRank).toLocaleString()} points
            toward {nextRank}.
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-7 pb-6">
          <Crown className="h-5 w-5 text-cyan-500 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-cyan-700">Maximum rank achieved</div>
            <div className="text-xs text-text-secondary mt-0.5">
              You&apos;ve reached the top tier. Keep showing up.
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
